import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useSubscriptionPlans from "@/hooks/useSubscriptionPlans";
import {
  Building2, TrendingUp, CreditCard, Clock, AlertTriangle, CheckCircle2,
  PauseCircle, Archive, Crown, Sparkles, Activity, Bell, ArrowUpRight,
  Calendar, Loader2, RefreshCw, Power, Coins, Users as UsersIcon, BarChart3,
  Infinity as InfinityIcon,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

/* ------------------------------------------------------------------ */
/* Small visual building blocks                                       */
/* ------------------------------------------------------------------ */

// Hero KPI tile. Keep it dense — 6 of these fit the operator's eye in one row.
const KpiTile = ({ Icon, label, value, sub, color = "var(--gold)", testid }) => (
  <div className="bg-white rounded-2xl border border-stone-100 p-4 shadow-sm hover:shadow-md transition" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">{label}</div>
      <Icon size={14} style={{ color }}/>
    </div>
    <div className="text-3xl font-bold text-stone-800 num-display mt-1 leading-none">{value}</div>
    {sub != null && <div className="text-[11px] text-stone-500 mt-1.5">{sub}</div>}
  </div>
);

// Status pill — single source of truth for tenant status visual.
const StatusBadge = ({ status, lang }) => {
  const map = {
    active: { c: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2, t: lang === "ar" ? "نشط" : "Active" },
    trial: { c: "bg-blue-100 text-blue-700", Icon: Sparkles, t: lang === "ar" ? "تجربة" : "Trial" },
    expired: { c: "bg-amber-100 text-amber-700", Icon: AlertTriangle, t: lang === "ar" ? "منتهٍ" : "Expired" },
    suspended: { c: "bg-rose-100 text-rose-700", Icon: PauseCircle, t: lang === "ar" ? "معلّق" : "Suspended" },
    archived: { c: "bg-stone-200 text-stone-600", Icon: Archive, t: lang === "ar" ? "مؤرشف" : "Archived" },
    disabled: { c: "bg-stone-200 text-stone-600", Icon: PauseCircle, t: lang === "ar" ? "معطّل" : "Disabled" },
  };
  const m = map[status] || map.active;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.c}`}>
      <m.Icon size={10}/>{m.t}
    </span>
  );
};

// Renewal row used inside the Pipeline card. Click = jump to tenant detail.
const PipelineRow = ({ row, lang }) => {
  const isAr = lang === "ar";
  const dl = row.days_left;
  const tone = dl < 0 ? "text-rose-600" : dl <= 7 ? "text-amber-600" : "text-stone-700";
  return (
    <Link
      to={`/platform-admin/tenants/${row.tenant_id}`}
      className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-stone-50 transition border border-transparent hover:border-stone-200"
      data-testid={`pipeline-row-${row.tenant_id}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Building2 size={14} className="text-stone-400 flex-shrink-0"/>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-stone-800 truncate">{row.name}</div>
          <div className="text-[10px] text-stone-400 uppercase tracking-wider">{row.plan} · {row.monthly_price > 0 ? `${row.monthly_price} QAR/mo` : isAr ? "بدون رسوم" : "no fee"}</div>
        </div>
      </div>
      <div className="text-end flex-shrink-0">
        <div className={`text-sm font-bold num-display ${tone}`}>
          {dl < 0 ? (isAr ? `${-dl}-` : `-${-dl}d`) : (isAr ? `${dl}ي` : `${dl}d`)}
        </div>
        <div className="text-[10px] text-stone-400">
          {row.ends_at ? new Date(row.ends_at).toLocaleDateString(isAr ? "ar-EG" : "en-US") : "—"}
        </div>
      </div>
    </Link>
  );
};

// Single-color simple bar fragment used in the plan-distribution + MRR
// breakdown bars. Avoids pulling a heavy chart lib for two stacked bars.
const StackedBar = ({ rows, total, lang }) => {
  if (!total) return <div className="text-xs text-stone-400 text-center py-4">{lang === "ar" ? "لا توجد بيانات" : "No data"}</div>;
  const palette = ["#C5A572", "#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];
  return (
    <div className="space-y-3">
      <div className="flex h-3 w-full rounded-full overflow-hidden bg-stone-100">
        {rows.map((r, i) => {
          const pct = (r.value / total) * 100;
          return (
            <div
              key={r.label}
              className="h-full transition-all"
              style={{ width: `${pct}%`, background: palette[i % palette.length] }}
              title={`${r.label}: ${r.value}`}
            />
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
        {rows.map((r, i) => (
          <div key={r.label} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: palette[i % palette.length] }}/>
            <span className="text-stone-700 font-semibold uppercase">{r.label}</span>
            <span className="text-stone-400 num-display ms-auto">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};


/**
 * SubscriptionTypesGrid — large prominent cards, one per active plan code.
 *
 * Joins the platform stats (subscriber count per plan code) with the
 * dynamic plan catalog (limits, price, display name). Each card is a
 * Link that deep-links into the Tenants list filtered by that plan.
 *
 * Sort order: catalog `sort_order` first, then any plan codes that have
 * tenants but no catalog row (legacy / free) appended at the end so the
 * operator can still spot them and migrate.
 */
const SubscriptionTypesGrid = ({ stats, lang }) => {
  const isAr = lang === "ar";
  const { plans } = useSubscriptionPlans({ activeOnly: false });

  const cards = useMemo(() => {
    const dist = stats?.plan_distribution || {};
    const mrr = stats?.mrr_by_plan || {};
    // Cataloged plans (sorted) first
    const known = (plans || []).slice().sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));
    const seen = new Set();
    const out = known.map(p => {
      seen.add(p.code);
      return {
        code: p.code,
        name_en: p.name_en, name_ar: p.name_ar,
        monthly_price: p.monthly_price,
        max_branches: p.max_branches,
        max_users: p.max_users,
        max_services: p.max_services,
        max_bookings_per_month: p.max_bookings_per_month,
        active: p.active,
        count: dist[p.code] || 0,
        mrr: mrr[p.code] || 0,
      };
    });
    // Orphan codes (legacy/free) that have tenants but aren't in the catalog
    Object.entries(dist).forEach(([code, count]) => {
      if (seen.has(code)) return;
      out.push({
        code, name_en: code, name_ar: code,
        monthly_price: 0, max_branches: null, max_users: null,
        max_services: null, max_bookings_per_month: null,
        active: false, count, mrr: mrr[code] || 0, orphan: true,
      });
    });
    return out;
  }, [stats, plans]);

  if (!cards.length) return null;

  const fmt = (v) => v === null || v === undefined
    ? <InfinityIcon size={12} className="inline text-emerald-600 -mt-0.5"/>
    : v;

  // Plan-aware accent colors — trial=blue, basic=gold, pro=indigo, ent=violet,
  // anything else = stone.
  const accent = (code) => ({
    trial:      { ring: "ring-blue-200",      bg: "from-blue-50 to-white",      text: "text-blue-700",      icon: "text-blue-500",      grad: "linear-gradient(135deg,#3B82F6,#60A5FA)" },
    basic:      { ring: "ring-amber-200",     bg: "from-amber-50 to-white",     text: "text-amber-700",     icon: "text-[var(--gold)]", grad: "linear-gradient(135deg,#C5A572,#E0C895)" },
    pro:        { ring: "ring-indigo-200",    bg: "from-indigo-50 to-white",    text: "text-indigo-700",    icon: "text-indigo-500",    grad: "linear-gradient(135deg,#6366F1,#818CF8)" },
    enterprise: { ring: "ring-violet-200",    bg: "from-violet-50 to-white",    text: "text-violet-700",    icon: "text-violet-500",    grad: "linear-gradient(135deg,#8B5CF6,#A78BFA)" },
  }[code] || { ring: "ring-stone-200", bg: "from-stone-50 to-white", text: "text-stone-700", icon: "text-stone-500", grad: "linear-gradient(135deg,#A8A29E,#D6D3D1)" });

  return (
    <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5" data-testid="subscription-types-grid">
      <div className="flex items-center gap-2 mb-4">
        <Crown size={18} className="text-[var(--gold)]"/>
        <h2 className="font-heading text-xl gold-text">{isAr ? "أنواع الاشتراكات" : "Subscription Types"}</h2>
        <span className="ms-auto text-[10px] uppercase tracking-wider text-stone-400">
          {cards.length} {isAr ? "خطة" : "plans"} · {cards.reduce((a, b) => a + b.count, 0)} {isAr ? "مشترك" : "subscribers"}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(p => {
          const a = accent(p.code);
          return (
            <Link
              key={p.code}
              to={`/platform-admin/tenants?plan=${encodeURIComponent(p.code)}`}
              className={`group relative overflow-hidden rounded-2xl border border-stone-100 bg-gradient-to-br ${a.bg} p-5 hover:shadow-lg hover:ring-2 ${a.ring} transition-all`}
              data-testid={`sub-type-card-${p.code}`}
            >
              {/* Decorative top gradient bar */}
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: a.grad }}/>
              {p.orphan && (
                <span className="absolute top-3 end-3 text-[9px] font-bold uppercase tracking-wider bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded">
                  {isAr ? "قديم" : "legacy"}
                </span>
              )}
              {!p.active && !p.orphan && (
                <span className="absolute top-3 end-3 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                  {isAr ? "معطّلة" : "off"}
                </span>
              )}

              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-400 font-semibold">
                <Crown size={11} className={a.icon}/>{p.code}
              </div>
              <div className={`font-heading text-2xl ${a.text} mt-1`}>{isAr ? p.name_ar || p.name_en : p.name_en}</div>

              {/* Big subscriber count */}
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-5xl font-bold num-display text-stone-800 leading-none">{p.count}</span>
                <span className="text-xs text-stone-500">{isAr ? "مشترك" : p.count === 1 ? "subscriber" : "subscribers"}</span>
              </div>

              {/* Price + monthly MRR */}
              <div className="mt-3 flex items-end justify-between text-xs">
                <div>
                  <div className="text-stone-400 text-[10px] uppercase tracking-wider">{isAr ? "السعر" : "Price"}</div>
                  <div className="text-stone-700 font-bold num-display">{p.monthly_price} <span className="text-[10px] text-stone-400">QAR/mo</span></div>
                </div>
                <div className="text-end">
                  <div className="text-stone-400 text-[10px] uppercase tracking-wider">MRR</div>
                  <div className={`font-bold num-display ${a.text}`}>{Math.round(p.mrr)} QAR</div>
                </div>
              </div>

              {/* Limits row — compact, with infinity glyph for unlimited */}
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-3 pt-3 border-t border-stone-100 text-[10px] text-stone-500">
                <span className="flex items-center gap-1"><Building2 size={9}/>{fmt(p.max_branches)} {isAr ? "فرع" : "br"}</span>
                <span className="flex items-center gap-1"><UsersIcon size={9}/>{fmt(p.max_users)} {isAr ? "م" : "u"}</span>
                <span className="flex items-center gap-1"><Sparkles size={9}/>{fmt(p.max_services)} {isAr ? "خد" : "sv"}</span>
                <span className="flex items-center gap-1"><Calendar size={9}/>{fmt(p.max_bookings_per_month)}/{isAr ? "ش" : "mo"}</span>
              </div>

              {/* Hover CTA */}
              <div className="absolute bottom-2 end-2 flex items-center gap-1 text-[10px] font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                <span className={a.text}>{isAr ? "عرض المشتركين" : "View subscribers"}</span>
                <ArrowUpRight size={12} className={a.text}/>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function PlatformAdminDashboard() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tenants/stats/overview");
      setStats(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load stats");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const planRows = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.plan_distribution || {})
      .map(([code, count]) => ({ label: code, value: count }))
      .sort((a, b) => b.value - a.value);
  }, [stats]);

  const mrrRows = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.mrr_by_plan || {})
      .map(([code, value]) => ({ label: code, value }))
      .sort((a, b) => b.value - a.value);
  }, [stats]);

  if (loading || !stats) {
    return (
      <div className="p-10 text-center text-stone-400 text-sm flex items-center justify-center gap-2">
        <Loader2 className="animate-spin" size={14}/>{isAr ? "جارٍ التحميل..." : "Loading..."}
      </div>
    );
  }

  const t = stats.totals;
  const pipeline = stats.renewal_pipeline || { "7d": [], "14d": [], "30d": [] };
  const expired = stats.expired || [];
  const inactive = stats.inactive_30d || [];

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="platform-admin-dashboard">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl gold-text">{isAr ? "لوحة المنصة" : "Platform Dashboard"}</h1>
          <p className="text-sm text-stone-500 mt-1">
            {isAr
              ? "نظرة لحظية على المستأجرين، الاشتراكات، والتجديدات القادمة."
              : "Live overview of tenants, subscriptions and upcoming renewals."}
          </p>
        </div>
        <button onClick={load} className="px-3 py-1.5 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50 text-xs flex items-center gap-1.5">
          <RefreshCw size={12}/>{isAr ? "تحديث" : "Refresh"}
        </button>
      </div>

      {/* Hero KPI strip — 6 tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiTile testid="kpi-total" Icon={Building2} label={isAr ? "إجمالي" : "Total"} value={t.total} sub={`${stats.new_30d} ${isAr ? "خلال 30 يوم" : "in 30d"}`} color="#C5A572"/>
        <Link to="/platform-admin/tenants?status=active" className="block"><KpiTile testid="kpi-active" Icon={CheckCircle2} label={isAr ? "نشط" : "Active"} value={t.active} sub={isAr ? "مستأجرون يدفعون" : "paying tenants"} color="#10B981"/></Link>
        <Link to="/platform-admin/tenants?status=trial" className="block"><KpiTile testid="kpi-trial" Icon={Sparkles} label={isAr ? "تجربة" : "Trial"} value={t.trial} sub={`${stats.new_trials_7d} ${isAr ? "جديد/أسبوع" : "new/week"}`} color="#3B82F6"/></Link>
        <Link to="/platform-admin/tenants?expiry=7d" className="block"><KpiTile testid="kpi-expiring" Icon={Clock} label={isAr ? "ينتهي قريباً" : "Expiring Soon"} value={stats.expiring_soon_count} sub={`${pipeline["7d"].length} ${isAr ? "خلال أسبوع" : "this week"}`} color="#F59E0B"/></Link>
        <Link to="/platform-admin/tenants?status=suspended" className="block"><KpiTile testid="kpi-suspended" Icon={PauseCircle} label={isAr ? "معلّق" : "Suspended"} value={(t.suspended || 0) + (t.archived || 0)} sub={isAr ? "خارج الخدمة" : "out of service"} color="#EF4444"/></Link>
        <KpiTile testid="kpi-mrr" Icon={Coins} label="MRR" value={`${Math.round(stats.mrr)} QAR`} sub={`${Object.keys(stats.mrr_by_plan).length} ${isAr ? "خطة مدفوعة" : "paid plans"}`} color="#8B5CF6"/>
      </div>

      {/* Expired alert banner — only when there's something to fix */}
      {expired.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-start gap-3" data-testid="expired-banner">
          <AlertTriangle size={20} className="text-rose-600 flex-shrink-0 mt-0.5"/>
          <div className="flex-1">
            <div className="font-bold text-rose-900 text-sm">
              {isAr ? `${expired.length} مستأجر منتهي الاشتراك` : `${expired.length} tenants past due`}
            </div>
            <p className="text-xs text-rose-700 mt-0.5">
              {isAr ? "هؤلاء المستأجرون لا يستطيعون تسجيل الدخول حالياً — يحتاجون تجديد فوري." : "These tenants are locked out and need renewal."}
            </p>
          </div>
          <Link to="/platform-admin/tenants?expiry=expired" className="text-xs font-semibold text-rose-700 hover:text-rose-900 flex items-center gap-1 self-center">
            {isAr ? "عرض" : "View"}<ArrowUpRight size={12}/>
          </Link>
        </div>
      )}

      {/* Subscription Types — large bold cards, one per plan code in use. */}
      {/* Click → deep-link to Tenants list filtered by that plan. */}
      <SubscriptionTypesGrid stats={stats} lang={lang}/>

      {/* Renewal Pipeline (left) + Plan Distribution + MRR Breakdown (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Pipeline — 2 columns wide */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-stone-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-[var(--gold)]"/>
              <h2 className="font-heading text-lg gold-text">{isAr ? "خط التجديدات" : "Renewal Pipeline"}</h2>
            </div>
            <span className="text-[10px] text-stone-400 uppercase tracking-wider">{stats.expiring_soon_count} {isAr ? "خلال 30 يوم" : "in 30d"}</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { key: "7d", label: isAr ? "خلال 7 أيام" : "Next 7 days", color: "border-rose-200 bg-rose-50/40", textColor: "text-rose-700" },
              { key: "14d", label: isAr ? "8 إلى 14 يوم" : "8-14 days", color: "border-amber-200 bg-amber-50/40", textColor: "text-amber-700" },
              { key: "30d", label: isAr ? "15 إلى 30 يوم" : "15-30 days", color: "border-stone-200 bg-stone-50/40", textColor: "text-stone-600" },
            ].map(b => (
              <div key={b.key} className={`rounded-xl border ${b.color} p-3`}>
                <div className="flex items-center justify-between mb-2">
                  <div className={`text-[10px] uppercase tracking-wider font-bold ${b.textColor}`}>{b.label}</div>
                  <div className={`text-2xl font-bold num-display ${b.textColor}`}>{pipeline[b.key].length}</div>
                </div>
                <div className="space-y-1 max-h-72 overflow-auto">
                  {pipeline[b.key].length === 0 ? (
                    <div className="text-[11px] text-stone-400 italic text-center py-3">{isAr ? "لا شيء" : "Empty"}</div>
                  ) : (
                    pipeline[b.key].map(r => <PipelineRow key={r.tenant_id} row={r} lang={lang}/>)
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Plan Distribution + MRR Breakdown — stacked right column */}
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5" data-testid="plan-distribution-card">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 size={16} className="text-[var(--gold)]"/>
              <h2 className="font-heading text-base gold-text">{isAr ? "توزيع الخطط" : "Plan Distribution"}</h2>
            </div>
            <StackedBar rows={planRows} total={t.total} lang={lang}/>
          </div>
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5" data-testid="mrr-breakdown-card">
            <div className="flex items-center gap-2 mb-3">
              <Coins size={16} className="text-[var(--gold)]"/>
              <h2 className="font-heading text-base gold-text">{isAr ? "MRR حسب الخطة" : "MRR by Plan"}</h2>
            </div>
            {mrrRows.length === 0 ? (
              <div className="text-xs text-stone-400 text-center py-4">{isAr ? "لا يوجد إيراد بعد" : "No revenue yet"}</div>
            ) : (
              <div className="space-y-2">
                {mrrRows.map(r => (
                  <div key={r.label} className="flex items-center justify-between">
                    <span className="text-xs text-stone-600 font-semibold uppercase">{r.label}</span>
                    <span className="text-sm font-bold text-stone-800 num-display">{r.value} QAR</span>
                  </div>
                ))}
                <div className="border-t border-stone-100 pt-2 mt-2 flex items-center justify-between">
                  <span className="text-xs text-stone-500 font-semibold">{isAr ? "الإجمالي" : "Total"}</span>
                  <span className="text-base font-bold gold-text num-display">{Math.round(stats.mrr)} QAR/mo</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent tenants + Inactive + Activity Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* New tenants */}
        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5" data-testid="recent-tenants-card">
          <div className="flex items-center gap-2 mb-3">
            <UsersIcon size={16} className="text-[var(--gold)]"/>
            <h2 className="font-heading text-base gold-text">{isAr ? "أحدث المستأجرين" : "Newest Tenants"}</h2>
          </div>
          <div className="space-y-1.5">
            {stats.recent_tenants.map(t => (
              <Link key={t.tenant_id} to={`/platform-admin/tenants/${t.tenant_id}`} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg hover:bg-stone-50">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-stone-800 truncate">{t.name}</div>
                  <div className="text-[10px] text-stone-400">
                    {t.owner || "—"} · {t.created_at ? new Date(t.created_at).toLocaleDateString(isAr ? "ar-EG" : "en-US") : "—"}
                  </div>
                </div>
                <StatusBadge status={t.status} lang={lang}/>
              </Link>
            ))}
          </div>
        </div>

        {/* Inactive tenants (signals) */}
        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5" data-testid="inactive-tenants-card">
          <div className="flex items-center gap-2 mb-3">
            <Power size={16} className="text-amber-500"/>
            <h2 className="font-heading text-base gold-text">{isAr ? "مستأجرون خاملون" : "Inactive Tenants"}</h2>
            <span className="ms-auto text-[10px] text-stone-400">{isAr ? "بدون نشاط 30+ يوم" : "30+ idle days"}</span>
          </div>
          {inactive.length === 0 ? (
            <div className="text-xs text-stone-400 text-center py-6 italic">
              {isAr ? "كل المستأجرين نشطون 🎉" : "All tenants active 🎉"}
            </div>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-auto">
              {inactive.map(t => (
                <Link key={t.tenant_id} to={`/platform-admin/tenants/${t.tenant_id}`} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg hover:bg-stone-50">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-stone-800 truncate">{t.name}</div>
                    <div className="text-[10px] text-stone-400">{t.plan}</div>
                  </div>
                  <div className="text-end flex-shrink-0">
                    <div className="text-sm font-bold text-amber-600 num-display">
                      {t.days_idle != null ? `${t.days_idle}${isAr ? "ي" : "d"}` : "∞"}
                    </div>
                    <div className="text-[10px] text-stone-400">{isAr ? "خامل" : "idle"}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Activity feed */}
        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5" data-testid="activity-feed-card">
          <div className="flex items-center gap-2 mb-3">
            <Activity size={16} className="text-[var(--gold)]"/>
            <h2 className="font-heading text-base gold-text">{isAr ? "النشاط الحديث" : "Recent Activity"}</h2>
          </div>
          {(stats.recent_activity || []).length === 0 ? (
            <div className="text-xs text-stone-400 text-center py-6 italic">{isAr ? "لا يوجد نشاط بعد" : "No activity yet"}</div>
          ) : (
            <div className="space-y-1 max-h-72 overflow-auto">
              {(stats.recent_activity || []).slice(0, 12).map((e, i) => (
                <div key={i} className="text-xs px-2 py-1.5 rounded-lg hover:bg-stone-50">
                  <div className="font-semibold text-stone-700 truncate">{e.action || e.summary || "event"}</div>
                  <div className="text-[10px] text-stone-400 flex items-center gap-1">
                    {e.tenant_id && <span className="font-mono">{e.tenant_id.slice(0, 14)}…</span>}
                    {e.ts && <span>· {new Date(e.ts).toLocaleString(isAr ? "ar-EG" : "en-US")}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
